/**
 * Analytics tooltip for a student's custom pages in one lesson.
 *
 * Unlike an exit ticket (one assessment per lesson), a lesson may hold several
 * custom pages, each with its own attempt history. Each page therefore renders
 * as its own block: collapsed it shows the student's best attempt using the
 * shared AssessmentAttemptCard, and clicking the header expands every attempt
 * for that page, newest first.
 */
import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useScrollHint } from "../../../../hooks/useScrollHint";
import api from "../../../../utils/axios-utils";
import AssessmentAttemptCard from "./AssessmentAttemptCard";

type CustomPageAttempt = {
  attempt_id: number;
  attempt_number: number;
  score: number | null;
  total_questions: number;
  correct_count: number;
  status: string;
  submitted_at: string | null;
};

type CustomPage = {
  exit_ticket_id: number;
  name: string | null;
  slide_index: number;
  attempts: CustomPageAttempt[];
};

const submittedMs = (a: CustomPageAttempt): number =>
  a.submitted_at ? new Date(a.submitted_at).getTime() : 0;

const completedOf = (attempts: CustomPageAttempt[]): CustomPageAttempt[] =>
  attempts.filter((a) => a.status === "completed");

/** Highest score wins; ties break toward the later submission. */
const pickBest = (attempts: CustomPageAttempt[]): CustomPageAttempt | null => {
  const completed = completedOf(attempts);
  if (completed.length === 0) return null;
  return completed.reduce((best, a) => {
    const bestScore = best.score ?? -1;
    const score = a.score ?? -1;
    if (score > bestScore) return a;
    if (score === bestScore && submittedMs(a) > submittedMs(best)) return a;
    return best;
  });
};

const PageBlock: React.FC<{
  page: CustomPage;
  onViewDetails: (attemptId: number) => void;
}> = ({ page, onViewDetails }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const pageName = page.name || `Custom Page ${page.slide_index + 1}`;
  const completed = completedOf(page.attempts);
  const inProgressCount = page.attempts.length - completed.length;
  const best = pickBest(page.attempts);
  const isMultiple = completed.length > 1;

  const sorted = [...completed].sort((a, b) => submittedMs(b) - submittedMs(a));
  const shown =
    isMultiple && !isExpanded ? [best as CustomPageAttempt] : sorted;

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        disabled={!isMultiple}
        aria-expanded={isMultiple ? isExpanded : undefined}
        onClick={() => setIsExpanded((v) => !v)}
        className={`flex items-center justify-between gap-3 w-full text-left ${
          isMultiple ? "cursor-pointer" : "cursor-default"
        }`}
      >
        <span className="font-bold text-base text-[#252525]">{pageName}</span>
        {isMultiple && (
          <span className="flex items-center gap-1 text-[#777777] text-xs whitespace-nowrap">
            {completed.length} attempts
            <ChevronDown
              size={12}
              strokeWidth={3}
              className={`transition-transform duration-150 ${
                isExpanded ? "rotate-180" : ""
              }`}
            />
          </span>
        )}
      </button>

      {completed.length === 0 && page.attempts.length > 0 && (
        <span className="text-[#777] text-xs">In Progress</span>
      )}
      {page.attempts.length === 0 && (
        <span className="et-analytics-placeholder text-[#B4B4B4] text-xs">
          Not Started
        </span>
      )}

      {shown.map((a) => (
        <AssessmentAttemptCard
          key={a.attempt_id}
          attemptId={a.attempt_id}
          attemptNumber={a.attempt_number}
          score={a.score}
          totalQuestions={a.total_questions}
          correctCount={a.correct_count}
          isBest={best?.attempt_id === a.attempt_id}
          showHeader={isMultiple}
          showMastery={false}
          onViewDetails={onViewDetails}
        />
      ))}

      {/* Not gated on isExpanded: the header button that toggles it is disabled
          unless there are 2+ completed attempts, so a page with one completed
          attempt plus an in-progress one would never show this. */}
      {completed.length > 0 && inProgressCount > 0 && (
        <span className="text-[#777] text-xs">
          + {inProgressCount} in progress
        </span>
      )}
    </div>
  );
};

/**
 * Mirrors the loaded layout — heading, page title, attempt card — so the
 * tooltip does not jump when the fetch lands. It also gives TooltipWrapper a
 * realistic height to measure, which is what decides whether the card opens
 * upward or downward.
 */
const CustomPagesSkeleton: React.FC = () => (
  // Nesting and spacing mirror CustomPagesList → PageBlock →
  // AssessmentAttemptCard exactly; each bar's height is the line-height of the
  // text it stands in for (text-sm 20px, text-base 24px, text-xl 28px).
  <div className="font-mulish text-sm animate-pulse">
    <div className="h-5 w-36 bg-gray-200 rounded mb-3" />
    <div className="flex flex-col gap-4 p-1">
      <div className="flex flex-col gap-2">
        <div className="h-6 w-48 bg-gray-200 rounded" />
        <div className="rounded-[10px] mb-[10px] py-[10px] px-[15px] flex flex-col gap-[10px] bg-white shadow-[0_0_5px_rgba(12,113,78,0.2)]">
          <div className="h-7 w-32 bg-gray-200 rounded" />
          <div className="h-5 w-28 bg-gray-200 rounded" />
        </div>
      </div>
    </div>
  </div>
);

const CustomPagesList: React.FC<{
  pages: CustomPage[];
  studentName: string;
  onViewDetails: (attemptId: number) => void;
}> = ({ pages, studentName, onViewDetails }) => {
  const firstName = studentName.split(" ")[0];
  const {
    ref: scrollRef,
    hasMore: hasMoreBelow,
    onScroll,
    checkScroll,
  } = useScrollHint<HTMLDivElement>({ axis: "vertical" });

  useEffect(() => {
    requestAnimationFrame(checkScroll);
  }, [checkScroll, pages]);

  if (pages.length === 0) {
    return (
      <div className="font-mulish text-sm text-[#777]">
        No custom pages found.
      </div>
    );
  }

  return (
    <div className="font-mulish text-sm">
      <div className="text-[#777777] mb-3">
        {firstName}&rsquo;s custom pages
      </div>
      {hasMoreBelow && (
        <span className="block mb-1 font-mulish et-analytics-caption text-[10px] leading-none et-analytics-muted text-[#25252566]">
          Scroll to see all {pages.length} pages ↓
        </span>
      )}
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex flex-col gap-4 max-h-[400px] overflow-y-auto p-1"
      >
        {pages.map((p) => (
          <PageBlock
            key={p.exit_ticket_id}
            page={p}
            onViewDetails={onViewDetails}
          />
        ))}
      </div>
    </div>
  );
};

interface CustomTicketPopoverProps {
  lessonId: number;
  userId: number;
  studentName: string;
  onViewDetails: (attemptId: number) => void;
}

const CustomTicketPopover: React.FC<CustomTicketPopoverProps> = ({
  lessonId,
  userId,
  studentName,
  onViewDetails,
}) => {
  const [pages, setPages] = useState<CustomPage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchPages = async () => {
      try {
        const resp = await api.get(
          `/exit-ticket/custom/lesson/${lessonId}/all`,
          {
            params: { user_id: userId },
          },
        );
        if (!cancelled) setPages(resp.data.pages ?? []);
      } catch (err) {
        console.error("Failed to fetch custom pages:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPages();
    return () => {
      cancelled = true;
    };
  }, [lessonId, userId]);

  if (loading) return <CustomPagesSkeleton />;

  return (
    <CustomPagesList
      pages={pages}
      studentName={studentName}
      onViewDetails={onViewDetails}
    />
  );
};

export default CustomTicketPopover;
